import React from 'react';
import { Scale, Phone, Mail, MapPin, ExternalLink, ChevronUp, Facebook, Twitter, Instagram, Youtube } from 'lucide-react';
import { Button } from './ui/button';

export function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    'About Us',
    'Court Divisions',
    'E-Filing Portal', 
    'Case Search',
    'Court Calendar',
    'Forms & Documents',
    'Fee Schedule',
    'Contact Us'
  ];

  const legalLinks = [
    'Privacy Policy',
    'Terms of Service', 
    'Accessibility',
    'Court Rules',
    'Legal Notices',
    'Freedom of Information'
  ];

  const socialLinks = [
    { icon: <Facebook className="h-5 w-5" />, href: '#', label: 'Facebook' },
    { icon: <Twitter className="h-5 w-5" />, href: '#', label: 'Twitter' },
    { icon: <Instagram className="h-5 w-5" />, href: '#', label: 'Instagram' },
    { icon: <Youtube className="h-5 w-5" />, href: '#', label: 'YouTube' }
  ];

  return (
    <footer className="bg-blue-900 text-white overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Main Footer */}
        <div className="py-16 grid lg:grid-cols-4 md:grid-cols-2 gap-8">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              
              <div>
                <h3 className="text-2xl font-bold">LAGOS STATE JUDICIARY</h3>
              </div>
            </div>
            

            {/* Contact Info */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-blue-400" />
                <div>
                  <p className="text-sm text-blue-300">Phone</p>
                  <a href="tel:+2348164633298" className="hover:text-blue-200">
                    +2348164633298
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-blue-400" />
                <div>
                  <p className="text-sm text-blue-300">Email</p>
                  <a href="mailto:info@lagosjudiciary.gov.ng" className="hover:text-blue-200">
                    Support@lagosjudiciary.gov.ng
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-blue-400 mt-1" />
                <div>
                  <p className="text-sm text-blue-300">Address</p>
                  <address className="not-italic">
                    Oba Akinjobi Way<br/> 
                    Ikeja, Lagos.
                  </address>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 bg-blue-800 hover:bg-blue-700 rounded-full flex items-center justify-center transition-colors"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={`#${link.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="text-blue-200 hover:text-white transition-colors text-sm"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-lg mb-6">Legal & Policies</h4>
            <ul className="space-y-3">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={`#${link.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="text-blue-200 hover:text-white transition-colors text-sm"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
            
            {/* Emergency Info */}
            <div className="mt-8 p-4 bg-red-900/30 border border-red-700 rounded-lg">
              <h5 className="text-red-200 mb-2 font-medium">Emergency Contacts</h5>
              <p className="text-red-100 text-sm">Emergency: 199</p>
              <p className="text-red-100 text-sm">Legal Aid: +234 800 LEGAL AID</p>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-blue-800 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-blue-200 text-sm">
                © {new Date().getFullYear()} Lagos State Judiciary. All rights reserved. 
              </p>
              <p className="text-blue-300 text-xs mt-1">
                Powered by First Hightower Infotech Ltd.
              </p>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-blue-200">
              <span>Follow us for updates</span>
              <ExternalLink className="h-4 w-4" />
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <Button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 bg-orange-500 hover:bg-orange-600 text-white rounded-full p-4 shadow-2xl z-50"
        size="sm"
        aria-label="Back to top"
      >
        <ChevronUp className="h-6 w-6" />
      </Button>
    </footer>
  );
}
